import type { OperatorIdentity } from "@billiard/contracts";
import { useId } from "react";
import { Link } from "react-router-dom";
import { ServiceDutyWorkCenterPanel } from "./ServiceDutyDesk";
import "./RoleHome.css";

export type RoleHomeRole = "OWNER" | "MANAGER" | "CASHIER" | "WAITER" | "COACH";

interface RoleHomeAction {
  to: string;
  title: string;
  description: string;
  group: "TODAY" | "MANAGEMENT" | "PERSONAL" | "SUPPORT";
  roles: readonly RoleHomeRole[];
}

const roleLabels: Record<RoleHomeRole, string> = {
  OWNER: "老板",
  MANAGER: "店长",
  CASHIER: "前台收银员",
  WAITER: "服务员",
  COACH: "助教",
};

const groupLabels: Record<RoleHomeAction["group"], { title: string; description: string }> = {
  TODAY: { title: "今天要办", description: "当前角色最常用的营业入口，点击后直接开始办理。" },
  MANAGEMENT: {
    title: "门店与人员",
    description: "经营设置和人员权限分开显示，不与技术维护混在一起。",
  },
  PERSONAL: { title: "我的工作", description: "只查看或处理与本人、本班相关的事项。" },
  SUPPORT: {
    title: "技术支持",
    description: "查看设备和连接状态；不会在这里办理日常经营或修改账目。",
  },
};

const actions: readonly RoleHomeAction[] = [
  {
    to: "/owner",
    title: "查看经营情况",
    description: "查看本店营业、资金、库存和待处理提醒。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER"],
  },
  {
    to: "/frontdesk",
    title: "前台营业",
    description: "选桌、开台、加单、收款和结账。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER"],
  },
  {
    to: "/review",
    title: "处理待办与审批",
    description: "核对退款、异常和需要人工确认的经营事项。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER"],
  },
  {
    to: "/shifts",
    title: "开店与交接班",
    description: "开始班次、清点现金并完成交班。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER"],
  },
  {
    to: "/orders",
    title: "订单核对",
    description: "按原订单查看收费、退款和打印记录。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER"],
  },
  {
    to: "/storage",
    title: "顾客寄存与领取",
    description: "从已结清的会员订单办理寄存，或继续领取已有寄存物品。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER"],
  },
  {
    to: "/rentals",
    title: "借出与归还",
    description: "在活动台单办理可租物品借出，或归还已有租赁物品。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER"],
  },
  {
    to: "/products?view=PURCHASE_ORDERS&purchaseStage=RECEIVING",
    title: "采购收货",
    description: "打开已批准采购单，登记实收并核对库存、成本和应付。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER"],
  },
  {
    to: "/staff",
    title: "现场服务",
    description: "接收清洁、送货和顾客现场请求。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER", "WAITER"],
  },
  {
    to: "/coach",
    title: "助教工作",
    description: "查看本人或本店助教的排队、任务和服务状态。",
    group: "TODAY",
    roles: ["OWNER", "MANAGER", "CASHIER", "COACH"],
  },
  {
    to: "/announcements",
    title: "门店公告",
    description: "查看当班必须知道的通知和提醒。",
    group: "TODAY",
    roles: ["WAITER", "COACH"],
  },
  {
    to: "/configuration",
    title: "门店经营设置",
    description: "调整获准的经营规则；保存前会说明生效结果。",
    group: "MANAGEMENT",
    roles: ["OWNER", "MANAGER"],
  },
  {
    to: "/workforce",
    title: "人员与权限",
    description: "新增、编辑、启停员工，并核对账号和本店任职。",
    group: "MANAGEMENT",
    roles: ["OWNER", "MANAGER"],
  },
  {
    to: "/products",
    title: "商品与库存",
    description: "维护商品、入库、盘点并查看库存提醒。",
    group: "MANAGEMENT",
    roles: ["OWNER", "MANAGER"],
  },
  {
    to: "/attendance",
    title: "出勤与排班",
    description: "查看本人出勤；获授权人员可处理本店排班。",
    group: "PERSONAL",
    roles: ["MANAGER", "CASHIER", "WAITER", "COACH"],
  },
  {
    to: "/payroll",
    title: "工资确认",
    description: "查看本人工资单；老板和店长按权限复核。",
    group: "PERSONAL",
    roles: ["OWNER", "MANAGER", "CASHIER", "WAITER", "COACH"],
  },
  {
    to: "/me",
    title: "我的资料",
    description: "查看本人账号、任职和可办理事项。",
    group: "PERSONAL",
    roles: ["OWNER", "MANAGER", "CASHIER", "WAITER", "COACH"],
  },
  {
    to: "/system",
    title: "设备与连接状态",
    description: "查看脱敏健康状态和已授权的技术工具。",
    group: "SUPPORT",
    roles: ["OWNER", "MANAGER"],
  },
  {
    to: "/help",
    title: "使用说明",
    description: "按当前角色查看页面位置、用途和权限说明。",
    group: "SUPPORT",
    roles: ["OWNER", "MANAGER", "CASHIER", "WAITER", "COACH"],
  },
];

export function roleHomeActionsForRoles(
  roles: readonly RoleHomeRole[],
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED" = "STORE_ACCEPTED",
): readonly RoleHomeAction[] {
  return actions.filter(
    (action) =>
      action.roles.some((role) => roles.includes(role)) &&
      (authenticationEvidence === "STORE_ACCEPTED" ||
        (action.to !== "/workforce" && action.to !== "/me")),
  );
}

export function RoleHome({
  displayName,
  effectiveRoles,
  authenticationEvidence,
  sharedShift,
  operator,
  personalSession = false,
}: {
  displayName: string;
  effectiveRoles: readonly RoleHomeRole[];
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED";
  sharedShift: boolean;
  operator?: OperatorIdentity;
  personalSession?: boolean;
}): React.JSX.Element {
  const headingId = useId();
  const roleActions = roleHomeActionsForRoles(effectiveRoles, authenticationEvidence);
  const visibleActions = sharedShift
    ? roleActions.filter((action) =>
        ["/frontdesk", "/shifts", "/orders", "/review", "/help"].includes(action.to),
      )
    : roleActions;
  const roleSummary = effectiveRoles.map((role) => roleLabels[role]).join("、") || "暂无有效任职";

  return (
    <section className="role-home" aria-labelledby={headingId}>
      <header className="role-home-heading">
        <span className="eyebrow">我的首页</span>
        <h1 id={headingId}>{displayName}，今天从这里开始</h1>
        <p>
          当前本店身份：{roleSummary}。
          {sharedShift ? "公共收银设备只显示本班可用入口。" : "这里只显示你能使用的入口。"}
        </p>
        <div className="role-home-permission-note" role="note">
          这里只有当前账号能办理的事。即使直接输入无权页面的地址，也看不到或改动那里的经营资料。
        </div>
      </header>

      {operator && effectiveRoles.some((role) => role !== "COACH") ? (
        <section className="role-home-section" aria-label="今日现场服务职责">
          <ServiceDutyWorkCenterPanel
            operator={operator}
            effectiveRoles={effectiveRoles}
            personalSession={personalSession}
            simulated={authenticationEvidence === "SIMULATED"}
          />
        </section>
      ) : null}

      {(["TODAY", "MANAGEMENT", "PERSONAL", "SUPPORT"] as const).map((group) => {
        const groupActions = visibleActions.filter((action) => action.group === group);
        if (groupActions.length === 0) return null;
        return (
          <section className="role-home-section" aria-labelledby={`role-home-${group}`} key={group}>
            <div className="role-home-section-heading">
              <h2 id={`role-home-${group}`}>{groupLabels[group].title}</h2>
              <p>{groupLabels[group].description}</p>
            </div>
            <div className="role-home-grid">
              {groupActions.map((action) => (
                <Link className="role-home-action" to={action.to} key={action.to}>
                  <span>
                    <strong>{action.title}</strong>
                    <small>{action.description}</small>
                  </span>
                  <span className="role-home-action-next" aria-hidden="true">
                    进入
                  </span>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </section>
  );
}
